import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { type ChartDataset, type ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

import { ChartLegendComponent } from '../chart-legend/chart-legend.component';
import { LegendBase } from '../chart-legend/legend-base';
import { defaultTooltip } from '../constants';
import { ChartType } from '../enums';

import { defaultOptionDoughnutChart, emptyDoughnutPlugin } from './constants';
import { dataSetDoughnutStyle } from './helper';
import { emptyStateDoughnut } from './plugins/empty-state-doughnut';

export type Doughnut = {
  label: string;
  data: number;
  // Tooltip displayed when hovering the data, every row is a new line
  tooltipRows: string[];
  // Color of the data
  color: string;
};

type DataWithLabels = {
  datasets: ChartDataset<ChartType.Doughnut>[];
  labels: string[];
};

type DoughnutChartOptionsWithPlugins = ChartOptions<ChartType.Doughnut> & {
  plugins: { emptyDoughnut: any };
};

@Component({
  selector: 'myre-doughnut',
  templateUrl: './doughnut.component.html',
  styleUrls: ['./doughnut.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BaseChartDirective, ChartLegendComponent],
})
export class DoughnutComponent extends LegendBase<ChartType.Doughnut> {
  protected readonly _chartType: ChartType.Doughnut = ChartType.Doughnut;

  @Input()
  public set dataToCompare(data: Doughnut[]) {
    if (!data?.length) {
      return;
    }

    this._data = this._getData(data);
    this._options = this._getOption(data);
  }

  @Input()
  public size: number | null = null;

  /**
   * @ignore
   */
  protected _data: DataWithLabels;

  /**
   * @ignore
   */
  protected _options: DoughnutChartOptionsWithPlugins;

  /**
   * @ignore
   */
  constructor() {
    super();

    this._addPlugin(emptyStateDoughnut);
  }

  /**
   * @ignore
   */
  private _getData(data: Doughnut[]): DataWithLabels {
    data = this._normalizeSingleZeroData(data);

    return {
      labels: data.map(d => d.label),
      datasets: [
        {
          data: data.map(d => d.data),
          ...dataSetDoughnutStyle(data),
        },
      ],
    };
  }

  /**
   * @ignore
   */
  private _getOption(data: Doughnut[]): DoughnutChartOptionsWithPlugins {
    return {
      ...defaultOptionDoughnutChart,
      plugins: {
        emptyDoughnut: emptyDoughnutPlugin,
        tooltip: {
          callbacks: {
            label: ({ dataIndex }: { dataIndex: number }): string[] =>
              data[dataIndex].tooltipRows,
          },
          ...defaultTooltip,
        },
        legend: {
          display: false,
        },
      },
    };
  }

  /**
   * Forces the chart to display a 100% bar if there is only one data point and it is zero.
   */
  private _normalizeSingleZeroData(data: readonly Doughnut[]): Doughnut[] {
    if (data.length === 1 && data[0].data === 0) {
      return data.map(d => ({ ...d, data: 1 }));
    }

    return [...data];
  }
}
